import React, {useEffect, useState} from "react";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";
import HintBox from "@/src/components/Box/HintBox/HintBox";
import { Paper, Stack, Typography } from "@mui/material";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import JudicialProcessCustomerFeedbackForm from "./JudicialProcessCustomerFeedbackForm";
import useProductParams from "@/src/hooks/useProductParams";
import { getCaseLink, getProductData } from "@/src/services/productService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import { apiGet } from "@/src/services/apiClient";
import CostCalculatorTrafficResponse from "@/src/types/CostCalculatorTrafficResponse";
import FeedbackConfirmationPage from "@/src/components/Serviceworld/Feedback/FeedbackConfirmationPage";

export default function JudicialProcessCustomerFeedback() {
  const { productId } = useProductParams();
  const productApiUri = getProductData("traffic", "apiUri");
  const queryClient = useQueryClient();
  const [isLoadingFees, setIsLoadingFees] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [costCalculatorResponse, setCostCalculatorResponse] = useState<CostCalculatorTrafficResponse | null>(null);
  const { data: traffic, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId),
  });
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data),
  });

  const handleSubmit = async ({ values }: { values: Record<string, any> }) => {
    setIsSubmitting(true);
    await updateMutation.mutateAsync({
      id: traffic.id,
      uri: "/traffic",
      data: {
        trialWanted: values.trialWanted,
        trialWantedMessage: values.trialWantedMessage,
        triggerTrialWantedSubscriber: true,
      },
    });
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (traffic) {
      setIsLoadingFees(true);
      apiGet("/get_fees/" + traffic.id)
        .then((result) => {
          setCostCalculatorResponse(result);
        })
        .finally(() => setIsLoadingFees(false));
    }
  }, [traffic]);

  if (isLoading || isLoadingFees) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader centered/>
      </LayoutProtectedRoutes>
    );
  }

  if (!costCalculatorResponse || traffic.paymentType !== "private" || traffic.authorityDecision !== "court") {
    return null;
  }

  if (traffic.trialWanted) {
    return <FeedbackConfirmationPage caseLink={getCaseLink(traffic)} />;
  }

  return (
    <LayoutProtectedRoutes>
      <HintBox headline={"Ihre Rückmeldung wird benötigt"}>
        <Stack spacing={2}>
          <Typography>
            <strong>Nächster Schritt: Gerichtliche Verhandlung Ihres Verkehrsrechtsfalls</strong>
          </Typography>
          <Typography>
            Die Behörde hat Ihren Fall an das Gericht weitergegeben. Bitte geben Sie uns Rückmeldung darüber, ob Sie der weiteren Bearbeitung Ihres Falls
            zustimmen und diesen vor Gericht verhandeln wollen. <strong>Wichtig: Dabei werden weitere Kosten für Sie entstehen.</strong>
          </Typography>
          <Typography>
            <strong>Voraussichtliche Kosten gerichtliches Verfahren:</strong> Summe inkl. MwSt:{" "}
            {costCalculatorResponse.sumOfCostsCourtWithVat.toLocaleString("de-DE", {
              style: "currency",
              currency: "EUR",
            })}{" "}
            *
          </Typography>
          <Typography variant={"body3"}>*Kosten können je nach Verlauf des Verfahren variieren.</Typography>
        </Stack>
      </HintBox>
      <Paper sx={{ padding: "2rem" }}>
        <FormProvider initialValues={{ trialWantedMessage: "" }} submitAction={handleSubmit}>
          <JudicialProcessCustomerFeedbackForm isLoading={isSubmitting} />
        </FormProvider>
      </Paper>
    </LayoutProtectedRoutes>
  );
}
